import { BookCover } from "./BookCover";
import { BookOpenText, Check, ChevronDown, LogOut, Plus, Search, Settings2, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Project } from "../types";
import { Modal } from "./Modal";
import { useMenuKeyboard } from "../keyboard";

export type AppView = "workspace" | "reader" | "chapters" | "bible" | "agents" | "quality";

const names: Record<AppView, string> = {
  workspace: "创作台",
  reader: "沉浸阅读",
  chapters: "章节库",
  bible: "故事圣经",
  agents: "Agent 工作室",
  quality: "质量报告"
};

const navItems: Array<{ id: AppView; label: string }> = [
  { id: "workspace", label: "创作台" },
  { id: "chapters", label: "章节库" },
  { id: "bible", label: "故事圣经" },
  { id: "quality", label: "质量报告" },
  { id: "agents", label: "Agent 工作室" }
];

interface MastheadProps {
  view: AppView;
  project: Project;
  projects: Project[];
  onNavigate: (view: AppView) => void;
  onLogout: () => void;
  onSearch: (value: string) => void;
  onSelectProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => Promise<void>;
  onNewBook: () => void;
}

export function Masthead({ view, project, projects, onNavigate, onLogout, onSearch, onSelectProject, onDeleteProject, onNewBook }: MastheadProps) {
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const onSearchRef = useRef(onSearch); onSearchRef.current = onSearch;
  const deleteLock = useRef(false);
  const projectMenuRef = useRef<HTMLDivElement | null>(null);
  useMenuKeyboard(projectsOpen, ".project-menu", () => setProjectsOpen(false));

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.isComposing && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (!document.querySelector('[role="dialog"]')) onSearchRef.current("");
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !projectMenuRef.current?.contains(event.target)) setProjectsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  async function confirmDelete() {
    if (!deleteTarget || deleteText !== deleteTarget.title || deleteLock.current) return;
    deleteLock.current = true;
    setDeleting(true);
    try {
      await onDeleteProject(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // The app-level handler keeps the dialog open and surfaces the API error.
    } finally {
      deleteLock.current = false;
      setDeleting(false);
    }
  }

  return (
    <>
    <header className="masthead">
      <div className="masthead-inner">
        <button className="masthead-brand" type="button" onClick={() => onNavigate("workspace")} aria-label="墨砚 首页">
          <span className="brand-mark" aria-hidden="true">墨</span>
          <span className="masthead-name"><strong>墨砚</strong><small>AI NOVEL STUDIO</small></span>
        </button>

        <div className="topbar-project-wrap" ref={projectMenuRef}>
          <button
            className="topbar-project"
            type="button"
            onClick={() => setProjectsOpen((value) => !value)}
            onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); setProjectsOpen(true); } }}
            aria-expanded={projectsOpen}
            aria-haspopup="menu" aria-controls="project-menu"
          >
            <span className="topbar-project-book-icon" aria-hidden="true">
              <BookOpenText size={15} />
            </span>
            <span className="topbar-project-meta">
              <strong>{project.title}</strong>
              <small>{project.currentVolume} · {project.completedChapters} 章</small>
            </span>
            <ChevronDown size={14} className="topbar-project-arrow" />
          </button>
          {projectsOpen && (
            <>
              <div
                className="project-menu-backdrop"
                aria-hidden="true"
                onClick={() => setProjectsOpen(false)}
              />
              <div className="project-menu" id="project-menu" role="menu" aria-label="切换小说">
              <div className="project-menu-list">
                {projects.map((item) => (
                  <div className={`project-menu-item ${item.id === project.id ? "selected" : ""}`} role="none" key={item.id}>
                    <button
                      className="project-select-button"
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setProjectsOpen(false);
                        onSelectProject(item.id);
                      }}
                    >
                      <span className="project-menu-cover"><BookCover project={item} /></span>
                      <span><strong>{item.title}</strong><small>{item.completedChapters} / {item.targetChapters} 章 · {item.status}</small></span>
                      {item.id === project.id && <Check size={14} />}
                    </button>
                    <button
                      className="project-delete-button"
                      type="button" role="menuitem"
                      title={projects.length <= 1 ? "至少保留一个小说项目" : `删除《${item.title}》`}
                      aria-label={`删除《${item.title}》`}
                      disabled={projects.length <= 1}
                      onClick={() => {
                        setProjectsOpen(false);
                        setDeleteText(""); setDeleteTarget(item);
                      }}
                    >
                      <Trash2 size={15} />
                      <small style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: "1px", height: "1px" }}>操作</small>
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="new-book-button" role="menuitem"
                onClick={() => {
                  setProjectsOpen(false);
                  onNewBook();
                }}
              >
                <Plus size={16} />
                <span><strong>新建项目</strong><small>与新书主 Agent 讨论</small></span>
              </button>
            </div>
            </>
          )}
        </div>

        <nav className="masthead-nav" aria-label="主导航">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={view === item.id ? "active" : ""}
              onClick={() => onNavigate(item.id)}
              aria-label={item.label}
              aria-current={view === item.id ? "page" : undefined}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="masthead-tools">
          <button type="button" className="global-search ux-search-trigger" aria-label="搜索当前小说" onClick={() => onSearch("")}><Search size={16} /><span>搜索章节、设定、Agent…</span><kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K"}</kbd></button>
          <button className="mobile-search-trigger" type="button" aria-label="全局搜索" onClick={() => onSearch("")}><Search size={18} /></button>
          <button className="icon-button topbar-settings" type="button" title="设置" aria-label="设置" onClick={() => onNavigate("agents")}><Settings2 size={18} /></button>
          <button className="avatar-button" type="button" title="退出登录" onClick={onLogout} aria-label="退出登录">
            <span>作</span><LogOut size={14} />
          </button>
        </div>
      </div>
    </header>
    <Modal
      open={Boolean(deleteTarget)}
      busy={deleting}
      title="删除整本小说"
      description="此操作无法撤销"
      width="small"
      onClose={() => { if (!deleting) setDeleteTarget(null); }}
      footer={<>
        <button className="button button-ghost" type="button" disabled={deleting} onClick={() => setDeleteTarget(null)}>取消</button>
        <button className="button button-danger" type="button" disabled={deleting || deleteText !== deleteTarget?.title} onClick={() => { void confirmDelete(); }}><Trash2 size={15} />{deleting ? "删除中" : "确认删除"}</button>
      </>}
    >
      <div className="delete-project-warning">
        <strong>{deleteTarget ? `确定删除《${deleteTarget.title}》？` : "确定删除这本小说？"}</strong>
        <p>项目、章节正文、故事圣经、Agent 配置、评分记录和版本历史将被永久删除。</p>
        <label className="ux-search-field">请输入完整书名确认<input data-autofocus value={deleteText} disabled={deleting} onChange={(event) => setDeleteText(event.target.value)} placeholder={deleteTarget?.title} autoComplete="off" /></label>
      </div>
    </Modal>
    </>
  );
}
